import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TaskHardwareReservationIn } from '@app/interfaces/lm/task';
import { TimeSegment } from '@app/interfaces/reservation/human-reservation';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import { TaskService } from '@services/lm/task.service';
import { HardwareReservationService } from '@services/reservation/hardware-reservation.service';
import { HumanReservationService } from '@services/reservation/human-reservation.service';
import dayjs from 'dayjs';
import { ProjectCalendarComponent } from '@app/shared/components/project-calendar/project-calendar.component';

@Component({
    selector: 'app-calendar-dialog',
    templateUrl: './calendar-dialog.component.html',
    styleUrls: ['./calendar-dialog.component.less'],
})
export class CalendarDialogComponent implements OnInit {
    @ViewChild('projectCalendar') projectCalendar!: ProjectCalendarComponent;
    @Input() type = 'prime';
    @Input() showTaskRange = true;
    @Output() submitSuccess = new EventEmitter();
    calendarOptions = {
        events: [],
    } as CalendarOptions;
    isVisible = false;
    loading = false;
    title = '';
    id!: string;
    validateForm!: FormGroup;
    timeRange!: {
        startTime: string;
        endTime: string;
    };
    ready = false;

    constructor(private fb: FormBuilder,
                private hardwareReservationService: HardwareReservationService,
                private humanReservationService: HumanReservationService,
                private taskService: TaskService) {
    }

    ngOnInit(): void {
        this.validateForm = this.fb.group({
            taskDate: [[]],
        });
    }

    open(arg: { id: string, title: string }, timeRange: { startTime: string; endTime: string }): void {
        this.id = arg.id;
        this.isVisible = true;
        this.timeRange = timeRange;
        this.validateForm.get('taskDate')!.setValue([this.timeRange.startTime, this.timeRange.endTime]);
        this.title = arg.title;
        setTimeout(async () => {
            this.projectCalendar.setCalendarStart(this.timeRange.startTime);
            this.ready = true;
            this.type === 'hardware' ? await this.getHardwareData() : await this.getPrimeDetail();
        }, 400);
    }

    changeRange(range: string[]): void {
        if (!this.ready) {
            return;
        }
        this.timeRange = {
            startTime: range[0],
            endTime: range[1],
        };
        this.type === 'hardware' ? this.getHardwareData() : this.getPrimeDetail();
    }

    async getHardwareData(): Promise<void> {
        try {
            this.loading = true;
            const query = {
                hardwareIds: [this.id],
                startTime: dayjs(this.timeRange.startTime).valueOf(),
                endTime: dayjs(this.timeRange.endTime).valueOf(),
            };
            const data = await this.hardwareReservationService.findHardwareList(query);
            if (data.length === 0) {
                return;
            }
            const hardwareReservationIn = data.map(item => item.reservationId);
            const reservationInfo = await this.taskService.findHardwareReservation({
                hardwareReservationIn: hardwareReservationIn?.join(','),
            });
            this.formatData(data, reservationInfo);
        } finally {
            this.loading = false;
        }
    }

    formatData(data: Array<any>, reservationInfo: TaskHardwareReservationIn[] = []): void {
        const events: EventInput[] = [];
        data.forEach(item => {
            const timeSegments = this.type === 'prime' ? item.reservation.timeSegments : item.timeSegments;
            const projectInfo = reservationInfo.find(info => info.hardwareReservation === item.reservationId);
            events.push(...timeSegments?.map((rangeItem: TimeSegment) => {
                return {
                    title: projectInfo ? `${projectInfo.name}(${projectInfo.projectName})` : '',
                    start: dayjs(rangeItem.startTime).format('YYYY-MM-DD'),
                    end: dayjs(rangeItem.endTime).add(1, 'day').format('YYYY-MM-DD'),
                    allDay: true,
                    editable: false,
                    resourceEditable: false,
                };
            }));
        });
        if (events.length > 0) {
            this.calendarOptions.events = events;
        }
    }

    async getPrimeDetail(): Promise<void> {
        try {
            this.loading = true;
            const startTime = dayjs(this.timeRange.startTime).startOf('month').valueOf();
            const endTime = dayjs(this.timeRange.startTime).endOf('month').valueOf();
            const query = {
                humanIds: [this.id],
                startTime,
                endTime,
            };
            const data = await this.humanReservationService.getHumanUtilization(query);
            this.formatData(data);
        } finally {
            this.loading = false;
        }
    }

    close(): void {
        this.isVisible = false;
        this.calendarOptions.events = [];
        this.ready = false;
    }
}
